import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Extend ServiceWorkerRegistration to include pushManager (Web Push API)
declare global {
  interface ServiceWorkerRegistration {
    pushManager: PushManager;
  }
}

// VAPID public key for push notifications
const VAPID_PUBLIC_KEY = 'BLFxrUReVpi9POxZxsyqA3EVDnhs8gjJoDBZLR9PlegEsTEAzwoaaaZG9lHNA8WPGE-2S5Wts2rvHThekK8H0Qc';

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      console.log('[Push] Browser support check:', {
        serviceWorker: 'serviceWorker' in navigator,
        PushManager: 'PushManager' in window,
        Notification: 'Notification' in window,
        supported
      });
      
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        console.log('[Push] Current permission:', Notification.permission);
        await checkSubscription();
      }
      setIsLoading(false);
    };

    checkSupport();
  }, []);

  const checkSubscription = async () => {
    try {
      // First check if service worker is registered
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log('[Push] Service worker registrations:', registrations.length);
      
      if (registrations.length === 0) {
        console.log('[Push] No service worker registered yet');
        setIsSubscribed(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service worker ready:', registration.scope);
      
      const subscription = await registration.pushManager.getSubscription();
      console.log('[Push] Existing subscription:', subscription ? 'yes' : 'no');
      
      if (subscription) {
        console.log('[Push] Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...');
      }
      
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('[Push] Error checking subscription:', error);
      setIsSubscribed(false);
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Les notifications push ne sont pas supportées par votre navigateur');
      return false;
    }

    try {
      console.log('[Push] Starting subscription process...');
      
      // Request notification permission
      console.log('[Push] Requesting permission...');
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      console.log('[Push] Permission result:', permissionResult);

      if (permissionResult !== 'granted') {
        toast.error('Permission refusée pour les notifications');
        return false;
      }

      // Register service worker
      console.log('[Push] Registering service worker...');
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      console.log('[Push] Service worker registered:', registration.scope);
      
      // Wait for the service worker to be ready
      console.log('[Push] Waiting for service worker to be ready...');
      await navigator.serviceWorker.ready;
      console.log('[Push] Service worker is ready');

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('[Push] Already subscribed, using existing subscription');
      } else {
        // Subscribe to push notifications
        console.log('[Push] Creating new subscription with VAPID key...');
        console.log('[Push] VAPID key length:', VAPID_PUBLIC_KEY.length);
        
        const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        console.log('[Push] Application server key length:', applicationServerKey.byteLength);
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('[Push] New subscription created');
      }
      
      console.log('[Push] Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...');

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Vous devez être connecté');
        return false;
      }
      console.log('[Push] User ID:', user.id);

      // Save subscription to database
      const subscriptionJson = subscription.toJSON();
      console.log('[Push] Saving subscription to database...');
      console.log('[Push] Subscription keys:', {
        hasP256dh: !!subscriptionJson.keys?.p256dh,
        hasAuth: !!subscriptionJson.keys?.auth,
        p256dhLength: subscriptionJson.keys?.p256dh?.length,
        authLength: subscriptionJson.keys?.auth?.length
      });
      
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscriptionJson.endpoint!,
          p256dh: subscriptionJson.keys?.p256dh || '',
          auth: subscriptionJson.keys?.auth || ''
        }, {
          onConflict: 'user_id,endpoint'
        });

      if (error) {
        console.error('[Push] Database error:', error);
        throw error;
      }
      
      console.log('[Push] Subscription saved successfully');

      setIsSubscribed(true);
      toast.success('Notifications activées !');
      return true;
    } catch (error) {
      console.error('[Push] Error subscribing to push notifications:', error);
      toast.error('Erreur lors de l\'activation des notifications: ' + (error as Error).message);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    try {
      console.log('[Push] Starting unsubscription process...');
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        console.log('[Push] Unsubscribing from push...');
        await subscription.unsubscribe();

        // Remove from database
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          console.log('[Push] Removing from database...');
          await supabase
            .from('push_subscriptions')
            .delete()
            .eq('user_id', user.id)
            .eq('endpoint', subscription.endpoint);
        }
        console.log('[Push] Unsubscribed successfully');
      }

      setIsSubscribed(false);
      toast.success('Notifications désactivées');
      return true;
    } catch (error) {
      console.error('[Push] Error unsubscribing from push notifications:', error);
      toast.error('Erreur lors de la désactivation');
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe
  };
}
